import React, { useState, useRef } from 'react';
import portfolioData from '../data/drive_images.json';

const Portfolio = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Track loading state
  const imageCacheRef = useRef(new Set());

  // Preload images for a specific section
  const preloadImages = (images) => {
    images.forEach((src) => {
      if (!imageCacheRef.current.has(src)) {
        const img = new Image();
        img.onload = () => imageCacheRef.current.add(src);
        img.onerror = () => imageCacheRef.current.add(src);
        img.src = src;
      }
    });
  };

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setIsPortrait(naturalHeight / naturalWidth > 1.5);
    setIsLoading(false); // Hide spinner when image is ready
  };

  const sections = Object.keys(portfolioData).map(folderName => ({
    title: folderName,
    image: portfolioData[folderName][0],
    samples: portfolioData[folderName]
  }));

  const handleView = (section) => {
    setCurrentSection(section);
    setCurrentImageIndex(0);
    setShowPopup(true);
    preloadImages(section.samples);
    const firstImage = section.samples[0];
    setIsLoading(!imageCacheRef.current.has(firstImage));
  };

  const handleNext = () => {
    if (!currentSection) return;
    const nextIndex = currentImageIndex + 1;
    if (nextIndex < currentSection.samples.length) {
      const nextImage = currentSection.samples[nextIndex];
      setIsLoading(!imageCacheRef.current.has(nextImage));
      setCurrentImageIndex(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (!currentSection) return;
    const prevIndex = currentImageIndex - 1;
    if (prevIndex >= 0) {
      const prevImage = currentSection.samples[prevIndex];
      setIsLoading(!imageCacheRef.current.has(prevImage));
      setCurrentImageIndex(prevIndex);
    }
  };

  const handleClose = () => {
    setShowPopup(false);
    setCurrentSection(null);
    setCurrentImageIndex(0);
  };

  return (
    <div className="font-Poppins py-20 px-2 sm:px-6 lg:px-32 bg-gray-100 text-slate-900 min-h-screen dark:bg-slate-950 dark:text-gray-100">
      <h1 className="text-4xl font-bold text-center mb-8">Portfolio</h1>
      <div className="w-full flex justify-center items-center">
        <div className="h-1.5 w-36 bg-orange-500 mb-12 rounded-lg"></div>
      </div>

      {/* Sections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {sections.map((section, index) => (
          <div
            key={index}
            className="relative h-64 bg-cover bg-center rounded-lg shadow-lg group overflow-hidden cursor-pointer items-center"
            style={{ backgroundImage: `url(${section.image})` }}
            onClick={() => handleView(section)}
          >
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all duration-300"></div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-white item-center justify-center">
              <h2 className="text-2xl font-bold mb-4 self-center text-center">{section.title}</h2>
              <span className="bg-white text-black font-semibold py-2 px-6 rounded-lg dark:bg-slate-800 dark:text-white">View</span>
            </div>
          </div>
        ))}
      </div>

      {/* Popup Overlay */}
      {showPopup && currentSection && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          
          {/* Loading Spinner */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-white mt-4 font-medium">Loading Image...</p>
            </div>
          )}

          <div className="relative w-full h-full flex justify-center items-center">
            <div className={`transition-all duration-500 flex justify-center items-center
              ${isPortrait 
                ? 'w-[320px] h-[550px] md:w-[450px] md:h-[80vh] rounded-2xl border-4 border-gray-800 shadow-2xl overflow-hidden bg-gray-900' 
                : 'w-full h-full'
              }`}
            >
              <img
                onLoad={handleImageLoad}
                src={currentSection.samples[currentImageIndex]}
                alt="Portfolio Detail"
                className={`transition-opacity duration-500 ${isLoading ? 'opacity-0' : 'opacity-100'} ${
                  isPortrait 
                    ? 'w-full h-full object-cover'
                    : 'max-w-full max-h-[90vh] object-contain'
                }`}
              />
            </div>

            {/* Controls */}
            <button onClick={handleClose} className="absolute top-4 right-4 text-white text-4xl hover:text-orange-500 z-50">&times;</button>
            
            <button
              onClick={handlePrevious}
              disabled={currentImageIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl p-4 disabled:opacity-20 hover:text-orange-500 transition-all"
            >
              &#8249;
            </button>

            <button
              onClick={handleNext}
              disabled={currentImageIndex === currentSection.samples.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl p-4 disabled:opacity-20 hover:text-orange-500 transition-all"
            >
              &#8250;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;